import { supabase } from './supabaseClient';
import { decodeJwtParts } from './debug/jwtDebug';
import { normalizeEnvKey } from './envKeyUtils';
import { getValidAccessToken } from './getValidAccessToken';

const getFunctionsUrl = (slug) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${slug}`;
};

const buildAuthHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Edge Functions gateway expects the `apikey` header to be the legacy JWT-style anon key.
  // If the project uses a new `sb_publishable_...` key, sending it as `apikey` can cause 401 "Invalid JWT".
  // Follow the same safe logic used in riddleApi.js: only send apikey if it actually decodes as a JWT.
  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 && !jwtAnonDecoded?.decodeError && jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 && !anonDecoded?.decodeError && anonDecoded?.payload;

  // Prefer explicit legacy JWT anon key; otherwise only use REACT_APP_SUPABASE_ANON_KEY if it's JWT-shaped.
  const apikeyToSend = jwtAnonIsUsableJwt ? jwtAnonRaw : anonLooksJwt ? anonRaw : null;

  const accessToken = await getValidAccessToken();

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }

  return headers;
};

const getGenerationsApiUrl = () => {
  if (process.env.REACT_APP_GENERATIONS_API_URL) {
    const overrideUrl = String(process.env.REACT_APP_GENERATIONS_API_URL).trim();
    // Legacy safeguard: this app now serves generations via Supabase Edge Functions.
    // If an old env var still points to a removed /api/* route, fall back safely.
    if (overrideUrl.startsWith('/api/')) {
      return getFunctionsUrl('generations-image');
    }
    return overrideUrl;
  }
  // Default to Supabase Edge Function (long timeouts, best for Flux Pro).
  return getFunctionsUrl('generations-image');
};

/**
 * Generate an AI image for the given word.
 * @param {Object} opts
 * @param {string} opts.word - The 5-letter word.
 * @param {boolean} [opts.prefetch=false] - If true, the server skips the coin charge.
 *   Coins should be charged on the client when the buffered round is consumed.
 */
export const generateFalImageForWord = async ({ word, prefetch = false }) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const doRequest = async (reqHeaders) =>
    await fetch(getGenerationsApiUrl(), {
      method: 'POST',
      headers: reqHeaders,
      body: JSON.stringify({ word, prefetch }),
    });

  let response = await doRequest(headers);

  // If the access token expired while the app was backgrounded, refresh once and retry.
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const text = await response.text();
  let dataJson = null;
  try {
    dataJson = text ? JSON.parse(text) : null;
  } catch {
    dataJson = null;
  }

  if (!response.ok) {
    const error = new Error(dataJson?.error || text || 'Failed to generate image.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  if (!dataJson?.imageUrl || !dataJson?.word) {
    const error = new Error('No image returned.');
    error.code = 'NO_IMAGE';
    error.status = 502;
    throw error;
  }

  return dataJson;
};

